import React, { useState, useEffect } from "react"
import { Link } from "gatsby"
import PropTypes from "prop-types"
import styled from "styled-components"
import tw, { css } from "twin.macro"
import { CSSTransition, TransitionGroup } from "react-transition-group"

import { Menu } from "@components"
import { navLinks, email, colors } from "@config"
import { useScrollDirection } from "@hooks"
import { ButtonSecondary } from "@templates"
import { loaderDelay } from "@utils"
import {
  LogoIcon,
  HomeIcon,
  AboutIcon,
  WorkIcon,
  ProjectIcon,
} from "@components/icons"

const StyledHeader = styled.header`
  ${tw`fixed top-0 inset-x-0 w-full bg-neutralDark flex justify-center z-40 h-28 text-sm`}
  transition: all .25s var(--easing);

  ${props =>
    props.scrollDirection === "up" &&
    !props.scrolledToTop &&
    tw`h-28 translate-y-0`}

  ${props =>
    props.scrollDirection === "down" &&
    !props.scrolledToTop &&
    css`
      ${tw`h-28`} transform: translateY(calc(7rem * -1));
    `}
`

const StyledNavBar = styled.section`
  ${tw`h-full px-8 flex items-center justify-between w-full md:px-4 xl:px-0 xl:translate-x-0`}
  max-width: 1180px;
  & {
    .logo {
      a {
        svg {
          width: 42px;
          height: 42px;

          @media (min-width: 768px) {
            width: 48px;
            height: 48px;
          }
        }
      }
    }
  }
`
const StyledNavigation = styled.nav`
  ${tw`
    hidden items-center
    md:flex md:relative md:shadow-none md:h-full
  `}
  @media (min-width: 768px) {
    background-color: transparent;
    width: 100%;
  }
  & {
    .nav {
      ${tw`
        flex items-center justify-between list-none w-full m-0 p-0 
        md:justify-start md:gap-10 lg:gap-20 md:ml-12
      `}
      .nav-items {
        a {
          ${tw`flex text-neutralLight no-underline transition-colors hover:text-deepSky focus:text-deepSky`}
        }
      }
    }
  }
`

const Header = ({ isHome }) => {
  const [isMounted, setIsMounted] = useState(!isHome)
  const scrollDirection = useScrollDirection("down")
  const [scrolledToTop, setScrolledToTop] = useState(true)

  const handleScroll = () => {
    setScrolledToTop(window.pageYOffset < 50)
  }

  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 100)

    window.addEventListener("scroll", handleScroll)

    return () => {
      clearTimeout(timeout)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const timeout = isHome ? loaderDelay : 0
  const fadeClass = isHome ? "fade" : ""
  const fadeDownClass = isHome ? "fadedown" : ""

  function renderNavIcon(icon) {
    const navComps = {
      HomeIcon: <HomeIcon />,
      AboutIcon: <AboutIcon />,
      WorkIcon: <WorkIcon />,
      ProjectIcon: <ProjectIcon />,
    }

    // eslint-disable-next-line no-prototype-builtins
    return navComps.hasOwnProperty(icon) && navComps[icon]
  }

  return (
    <>
      <StyledHeader
        scrollDirection={scrollDirection}
        scrolledToTop={scrolledToTop}
      >
        <StyledNavBar>
          <TransitionGroup>
            {isMounted && (
              <CSSTransition classNames={fadeClass} timeout={timeout}>
                <div className="logo" tabIndex="-1">
                  {isHome ? (
                    <a href="/" aria-label="logo home">
                      <LogoIcon />
                    </a>
                  ) : (
                    <Link to="/" aria-label="logo home">
                      <LogoIcon />
                    </Link>
                  )}
                </div>
              </CSSTransition>
            )}
          </TransitionGroup>

          <StyledNavigation>
            <ul className="nav">
              <TransitionGroup component={null}>
                {isMounted &&
                  navLinks &&
                  navLinks.map(({ url, name }, i) => (
                    <CSSTransition
                      key={i}
                      classNames={fadeDownClass}
                      timeout={timeout}
                    >
                      <li
                        className="nav-items"
                        key={i}
                        style={{ transitionDelay: `${isHome ? i * 100 : 0}ms` }}
                      >
                        <Link to={url}>
                          <span className="nav-items__name">{name}</span>
                        </Link>
                      </li>
                    </CSSTransition>
                  ))}
              </TransitionGroup>
            </ul>
          </StyledNavigation>

          <TransitionGroup tw="ml-auto">
            {isMounted && (
              <CSSTransition classNames={fadeDownClass} timeout={timeout}>
                <div
                  style={{
                    transitionDelay: `${isHome ? navLinks.length * 100 : 0}ms`,
                  }}
                >
                  <ButtonSecondary reverse color={colors.cosmic}>
                    <a
                      href={`mailto:${email}`}
                      data-type="text"
                      className="btn__text"
                    >
                      Connect
                    </a>
                  </ButtonSecondary>
                </div>
              </CSSTransition>
            )}
          </TransitionGroup>
        </StyledNavBar>
      </StyledHeader>

      <TransitionGroup component={null}>
        {isMounted && navLinks && (
          <CSSTransition classNames={fadeDownClass} timeout={timeout}>
            <Menu renderNavIcon={renderNavIcon} />
          </CSSTransition>
        )}
      </TransitionGroup>
    </>
  )
}

Header.propTypes = {
  isHome: PropTypes.bool.isRequired,
}

export default Header
